/**
 * Customer Care Hub — Page-scoped interactions
 * FAQ category filtering, search, accordion, chip selection, smooth scrolling, video play
 */

(function () {
  'use strict';

  /* ---- FAQ Category Filtering ---- */
  const faqRoot = document.querySelector('[data-cc-faq]');
  if (faqRoot) {
    const catButtons = faqRoot.querySelectorAll('[data-cc-faq-cat]');
    const items = faqRoot.querySelectorAll('[data-cc-faq-item]');
    const searchInput = faqRoot.querySelector('[data-cc-faq-search]');
    const emptyMsg = faqRoot.querySelector('[data-cc-faq-empty]');
    let activeCategory = 'all';

    function filterFAQ() {
      const query = (searchInput ? searchInput.value : '').toLowerCase().trim();
      let visibleCount = 0;

      items.forEach(function (item) {
        const cat = item.getAttribute('data-category');
        const searchText = item.getAttribute('data-search-text') || '';
        const matchesCat = activeCategory === 'all' || cat === activeCategory;
        const matchesSearch = !query || searchText.indexOf(query) !== -1;
        const visible = matchesCat && matchesSearch;

        if (visible) {
          item.removeAttribute('hidden');
          visibleCount++;
        } else {
          item.setAttribute('hidden', '');
          item.removeAttribute('open');
        }
      });

      if (emptyMsg) {
        emptyMsg.classList.toggle('is-visible', visibleCount === 0);
      }
    }

    catButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        activeCategory = this.getAttribute('data-cc-faq-cat');
        catButtons.forEach(function (b) { b.classList.remove('is-active'); });
        this.classList.add('is-active');
        filterFAQ();

        // Scroll to first visible FAQ item so filtered results are in view
        var firstVisible = faqRoot.querySelector('.cc-faq__item:not([hidden])');
        if (firstVisible) {
          var faqList = faqRoot.querySelector('.cc-faq__list');
          var target = faqList || firstVisible;
          var rect = target.getBoundingClientRect();
          // Only scroll if the list top is above or below the viewport
          if (rect.top < 0 || rect.top > window.innerHeight * 0.5) {
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      });
    });

    if (searchInput) {
      let debounceTimer;
      searchInput.addEventListener('input', function () {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(filterFAQ, 200);
      });
    }
  }

  /* ---- Issue Chip Selection (Customer Care hub page) ---- */
  var repairsCta = document.querySelector('[data-cc-repairs-cta]');
  var repairsBaseHref = repairsCta ? repairsCta.getAttribute('href').split('?')[0] : '';

  document.querySelectorAll('[data-cc-chip]').forEach(function (chip) {
    chip.addEventListener('click', function () {
      var wasActive = this.getAttribute('aria-pressed') === 'true';
      // Deselect all siblings
      this.parentElement.querySelectorAll('[data-cc-chip]').forEach(function (c) {
        c.setAttribute('aria-pressed', 'false');
        c.classList.remove('is-active');
      });
      // Toggle this one
      if (!wasActive) {
        this.setAttribute('aria-pressed', 'true');
        this.classList.add('is-active');
        // Update CTA link with selected issue
        if (repairsCta) {
          repairsCta.href = repairsBaseHref + '?issue=' + encodeURIComponent(this.textContent.trim());
        }
      } else if (repairsCta) {
        repairsCta.href = repairsBaseHref;
      }
    });
  });

  /* ---- Pre-select issue chip on restoration form from ?issue= param ---- */
  var issueParam = new URLSearchParams(window.location.search).get('issue');
  if (issueParam) {
    var formChips = document.querySelectorAll('[data-cc-issue-chip]');
    var hiddenInput = document.querySelector('[data-cc-issue-value]');
    formChips.forEach(function (chip) {
      var match = chip.textContent.trim().toLowerCase() === issueParam.toLowerCase();
      chip.setAttribute('aria-pressed', match ? 'true' : 'false');
      chip.classList.toggle('is-active', match);
      if (match && hiddenInput) hiddenInput.value = chip.getAttribute('data-value');
    });
  }

  /* ---- Smooth Scroll for Anchor Links ---- */
  document.querySelectorAll('[data-smooth-scroll], .cc-ledger__row[href^="#"]').forEach(function (link) {
    link.addEventListener('click', function (e) {
      var href = this.getAttribute('href');
      if (!href || href.charAt(0) !== '#') return;
      var target = document.getElementById(href.substring(1));
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Update URL hash without jumping
        history.pushState(null, '', href);
      }
    });
  });

  /* ---- Video Play Button ---- */
  document.querySelectorAll('[data-cc-play]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var section = this.closest('.cc-sizing__media');
      if (!section) return;
      var video = section.querySelector('[data-cc-video]');
      if (!video) return;

      if (video.paused) {
        video.play();
        this.setAttribute('aria-label', 'Pause sizing guide video');
        this.querySelector('.cc-sizing__play-label').textContent = 'Pause';
      } else {
        video.pause();
        this.setAttribute('aria-label', 'Play sizing guide video');
        this.querySelector('.cc-sizing__play-label').textContent = btn.dataset.originalLabel || 'Watch · How to size, 30 sec';
      }
    });

    // Store original label
    var label = btn.querySelector('.cc-sizing__play-label');
    if (label) {
      btn.dataset.originalLabel = label.textContent;
    }
  });

  /* ---- Track Form — fetches order status from Railway backend ---- */
  var STATUS_API = 'https://ronaldo-customer-care-production.up.railway.app';

  document.querySelectorAll('[data-cc-track-form]').forEach(function (form) {
    var btn = form.querySelector('[data-cc-track-btn]');
    var input = form.querySelector('.cc-track__input');
    var errorEl = form.parentElement.querySelector('[data-cc-track-error]');
    var resultEl = form.parentElement.querySelector('[data-cc-track-result]');
    var nameEl = resultEl ? resultEl.querySelector('[data-cc-track-name]') : null;
    var labelEl = resultEl ? resultEl.querySelector('[data-cc-track-label]') : null;
    var stepperEl = resultEl ? resultEl.querySelector('[data-cc-stepper]') : null;
    var trackListEl = resultEl ? resultEl.querySelector('[data-cc-track-list]') : null;
    var btnText = btn ? btn.textContent : 'Track';

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var value = input ? input.value.trim() : '';
      if (!value) return;

      // Strip common prefixes — backend expects just the number
      var orderName = value.replace(/^(#|RJ-|RJ)/i, '');

      // Reset states
      if (errorEl) { errorEl.setAttribute('hidden', ''); errorEl.textContent = ''; }
      if (resultEl) resultEl.setAttribute('hidden', '');
      if (btn) { btn.textContent = 'Looking up…'; btn.classList.add('cc-track__submit--loading'); }

      fetch(STATUS_API + '/status/' + encodeURIComponent(orderName))
        .then(function (r) {
          if (r.status === 404) throw new Error('not_found');
          if (!r.ok) throw new Error('server');
          return r.json();
        })
        .then(function (data) {
          // Update stepper
          if (stepperEl) {
            var steps = stepperEl.querySelectorAll('[data-cc-step]');
            steps.forEach(function (step) {
              var idx = parseInt(step.getAttribute('data-cc-step'), 10);
              var active = idx <= data.step;
              var dot = step.querySelector('.cc-stepper__dot');
              var line = step.querySelector('.cc-stepper__line');
              var label = step.querySelector('.cc-stepper__label');

              if (dot) {
                dot.className = 'cc-stepper__dot ' + (active ? 'cc-stepper__dot--active' : 'cc-stepper__dot--inactive');
              }
              if (line) {
                line.className = 'cc-stepper__line ' + (active ? 'cc-stepper__line--active' : 'cc-stepper__line--inactive');
              }
              if (label) {
                label.className = 'cc-stepper__label ' + (active ? 'cc-stepper__label--active' : 'cc-stepper__label--inactive');
              }
            });
          }

          // Order name heading
          if (nameEl && data.order_name) {
            nameEl.textContent = 'Order ' + data.order_name;
          }

          // Status label
          if (labelEl) {
            labelEl.textContent = data.status_label;
          }

          // Tracking items
          if (trackListEl) {
            trackListEl.innerHTML = '';
            var items = data.tracking || [];

            // Fallback for legacy response without tracking array
            if (items.length === 0 && (data.tracking_url || data.tracking_number)) {
              items = [{
                tracking_number: data.tracking_number || '',
                tracking_url: data.tracking_url || '',
                carrier: '',
                shipment_status: ''
              }];
            }

            if (items.length > 0) {
              items.forEach(function (item) {
                var url = item.tracking_url || (item.tracking_number
                  ? 'https://tools.usps.com/go/TrackConfirmAction?tLabels=' + encodeURIComponent(item.tracking_number)
                  : '');
                var carrier = item.carrier || 'carrier';
                var statusText = item.shipment_status === 'delivered' ? ' · Delivered' : item.shipment_status === 'in_transit' ? ' · In transit' : '';

                var row = document.createElement('div');
                row.className = 'cc-track__tracking-item';

                var numSpan = document.createElement('span');
                numSpan.className = 'cc-track__tracking-number';
                numSpan.textContent = item.tracking_number;

                if (statusText) {
                  var statusSpan = document.createElement('span');
                  statusSpan.className = 'cc-track__tracking-status';
                  statusSpan.textContent = statusText;
                  numSpan.appendChild(statusSpan);
                }

                row.appendChild(numSpan);

                if (url) {
                  var link = document.createElement('a');
                  link.className = 'cc-track__tracking-link';
                  link.href = url;
                  link.target = '_blank';
                  link.rel = 'noopener';
                  link.textContent = 'Track with ' + carrier + ' \u2192';
                  row.appendChild(link);
                }

                trackListEl.appendChild(row);
              });
              trackListEl.removeAttribute('hidden');
            } else {
              trackListEl.setAttribute('hidden', '');
            }
          }

          if (resultEl) resultEl.removeAttribute('hidden');
        })
        .catch(function (err) {
          if (errorEl) {
            if (err.message === 'not_found') {
              errorEl.textContent = 'We couldn\u2019t find that order number. Double-check and try again — it\u2019s on your confirmation email.';
            } else {
              errorEl.textContent = 'Something went wrong. Please try again in a moment.';
            }
            errorEl.removeAttribute('hidden');
          }
        })
        .finally(function () {
          if (btn) { btn.textContent = btnText; btn.classList.remove('cc-track__submit--loading'); }
        });
    });
  });

  /* ---- Contact Form: Topic Switching ---- */
  var topicContainer = document.querySelector('[data-cc-topics]');
  if (topicContainer) {
    var topicButtons = topicContainer.querySelectorAll('[data-cc-form-topic]');
    var topicHint = document.querySelector('[data-cc-topic-hint]');
    var topicHintText = document.querySelector('[data-cc-topic-hint-text]');
    var topicHiddenInput = document.querySelector('[data-cc-topic-value]');
    var wholesaleFields = document.querySelector('[data-cc-wholesale-fields]');
    var donationFields = document.querySelector('[data-cc-donation-fields]');

    function setTopic(btn) {
      var topicId = btn.getAttribute('data-topic-id');
      var hint = btn.getAttribute('data-topic-hint');

      // Update active states
      topicButtons.forEach(function (b) {
        b.setAttribute('aria-pressed', 'false');
        b.classList.remove('is-active');
      });
      btn.setAttribute('aria-pressed', 'true');
      btn.classList.add('is-active');

      // Update hidden input
      if (topicHiddenInput) {
        topicHiddenInput.value = btn.textContent.trim();
      }

      // Update hint
      if (topicHint && topicHintText) {
        if (hint) {
          topicHintText.textContent = hint;
          topicHint.removeAttribute('hidden');
        } else {
          topicHint.setAttribute('hidden', '');
        }
      }

      // Conditional: wholesale fields
      if (wholesaleFields) {
        if (topicId === 'wholesale') {
          wholesaleFields.removeAttribute('hidden');
          wholesaleFields.querySelectorAll('input').forEach(function (input) {
            input.setAttribute('required', '');
          });
        } else {
          wholesaleFields.setAttribute('hidden', '');
          wholesaleFields.querySelectorAll('input').forEach(function (input) {
            input.removeAttribute('required');
            input.value = '';
          });
        }
      }

      // Conditional: donation fields (under "Something else")
      if (donationFields) {
        if (topicId === 'something-else') {
          donationFields.removeAttribute('hidden');
        } else {
          donationFields.setAttribute('hidden', '');
          donationFields.querySelectorAll('input').forEach(function (input) {
            input.value = '';
          });
        }
      }
    }

    topicButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        setTopic(this);
      });
    });

    // Initialize first topic
    if (topicButtons.length > 0) {
      setTopic(topicButtons[0]);
    }
  }

  /* ---- Contact Form: File Upload ---- */
  var fileInput = document.querySelector('[data-cc-file-input]');
  var dropzone = document.querySelector('[data-cc-dropzone]');
  var filePreview = document.querySelector('[data-cc-file-preview]');
  var fileName = document.querySelector('[data-cc-file-name]');
  var fileRemove = document.querySelector('[data-cc-file-remove]');

  if (fileInput && dropzone) {
    // Drag and drop
    ['dragenter', 'dragover'].forEach(function (evt) {
      dropzone.addEventListener(evt, function (e) {
        e.preventDefault();
        dropzone.classList.add('is-dragover');
      });
    });

    ['dragleave', 'drop'].forEach(function (evt) {
      dropzone.addEventListener(evt, function (e) {
        e.preventDefault();
        dropzone.classList.remove('is-dragover');
      });
    });

    dropzone.addEventListener('drop', function (e) {
      if (e.dataTransfer.files.length > 0) {
        fileInput.files = e.dataTransfer.files;
        showFilePreview(e.dataTransfer.files[0]);
      }
    });

    // File input change
    fileInput.addEventListener('change', function () {
      if (this.files.length > 0) {
        showFilePreview(this.files[0]);
      }
    });

    function showFilePreview(file) {
      if (filePreview && fileName) {
        fileName.textContent = file.name;
        filePreview.removeAttribute('hidden');
        dropzone.style.display = 'none';
      }
    }

    if (fileRemove) {
      fileRemove.addEventListener('click', function () {
        fileInput.value = '';
        if (filePreview) filePreview.setAttribute('hidden', '');
        dropzone.style.display = '';
      });
    }
  }

  /* ---- Contact Form: Submit with Photo Upload ---- */
  var contactForm = document.getElementById('cc-contact-form');
  if (contactForm) {
    var CONTACT_UPLOAD_API = 'https://ronaldo-customer-care-production.up.railway.app';
    var contactSubmitBtn = document.querySelector('[data-cc-contact-submit]');
    var contactPhotoUrl = document.querySelector('[data-cc-photo-url]');
    var contactFileInput = document.querySelector('[data-cc-file-input]');

    function contactUploadPhoto(file) {
      return fetch(CONTACT_UPLOAD_API + '/upload/stage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name, content_type: file.type })
      })
      .then(function (r) { return r.json(); })
      .then(function (staged) {
        var formData = new FormData();
        staged.parameters.forEach(function (p) {
          formData.append(p.name, p.value);
        });
        formData.append('file', file);
        return fetch(staged.url, { method: 'POST', body: formData })
          .then(function () { return staged.resourceUrl; });
      })
      .then(function (resourceUrl) {
        return fetch(CONTACT_UPLOAD_API + '/upload/finalize', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ resource_url: resourceUrl })
        })
        .then(function (r) { return r.json(); })
        .then(function (data) { return data.url; });
      });
    }

    contactForm.addEventListener('submit', function (e) {
      // Validate required fields (mirrors restoration/warranty form behavior)
      contactForm.querySelectorAll('.is-invalid').forEach(function (el) {
        el.classList.remove('is-invalid');
      });

      var valid = true;
      contactForm.querySelectorAll('[required]').forEach(function (input) {
        if (!input.value.trim() || (input.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.value.trim()))) {
          input.classList.add('is-invalid');
          valid = false;
        }
      });

      if (!valid) {
        e.preventDefault();
        var firstInvalid = contactForm.querySelector('.is-invalid');
        if (firstInvalid) firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      // If no photo, let native form submit
      if (!contactFileInput || contactFileInput.files.length === 0) return;

      // Photo present — intercept, upload, then submit
      e.preventDefault();
      if (contactSubmitBtn) {
        contactSubmitBtn.disabled = true;
        contactSubmitBtn.textContent = 'Uploading photo\u2026';
      }

      contactUploadPhoto(contactFileInput.files[0])
        .then(function (cdnUrl) {
          if (contactPhotoUrl) contactPhotoUrl.value = cdnUrl;
          // Clear file input so native submit doesn't try to send the file
          contactFileInput.value = '';
          if (contactSubmitBtn) contactSubmitBtn.textContent = 'Sending\u2026';
          contactForm.submit();
        })
        .catch(function (err) {
          console.error('Contact photo upload failed:', err);
          if (contactSubmitBtn) {
            contactSubmitBtn.disabled = false;
            contactSubmitBtn.textContent = 'Send message';
          }
          alert('Photo upload failed. You can try again or remove the photo to send without it.');
        });
    });
  }

  /* ---- Restoration Form: Issue Chips (multi-select) ---- */
  var issueContainer = document.querySelector('[data-cc-issue-chips]');
  if (issueContainer) {
    var issueChips = issueContainer.querySelectorAll('[data-cc-issue-chip]');
    var issueHidden = document.querySelector('[data-cc-issue-value]');

    function updateIssueValue() {
      if (!issueHidden) return;
      var selected = [];
      issueChips.forEach(function (c) {
        if (c.getAttribute('aria-pressed') === 'true') {
          selected.push(c.getAttribute('data-value'));
        }
      });
      issueHidden.value = selected.join(', ');
    }

    issueChips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        var pressed = this.getAttribute('aria-pressed') === 'true';
        this.setAttribute('aria-pressed', pressed ? 'false' : 'true');
        this.classList.toggle('is-active', !pressed);
        updateIssueValue();
      });
    });
  }

  /* ---- Restoration Form: Multi-File Upload ---- */
  var multiInput = document.querySelector('[data-cc-multi-file-input]');
  var multiDropzone = document.querySelector('[data-cc-multi-dropzone]');
  var filesList = document.querySelector('[data-cc-files-list]');
  var photosError = document.querySelector('[data-cc-photos-error]');
  var selectedFiles = [];

  if (multiInput && multiDropzone && filesList) {
    ['dragenter', 'dragover'].forEach(function (evt) {
      multiDropzone.addEventListener(evt, function (e) {
        e.preventDefault();
        multiDropzone.classList.add('is-dragover');
      });
    });

    ['dragleave', 'drop'].forEach(function (evt) {
      multiDropzone.addEventListener(evt, function (e) {
        e.preventDefault();
        multiDropzone.classList.remove('is-dragover');
      });
    });

    multiDropzone.addEventListener('drop', function (e) {
      addFiles(e.dataTransfer.files);
    });

    multiInput.addEventListener('change', function () {
      addFiles(this.files);
      this.value = '';
    });

    function addFiles(newFiles) {
      for (var i = 0; i < newFiles.length && selectedFiles.length < 3; i++) {
        selectedFiles.push(newFiles[i]);
      }
      renderFilesList();
      if (photosError) photosError.classList.remove('is-visible');
    }

    function renderFilesList() {
      filesList.innerHTML = '';
      selectedFiles.forEach(function (file, idx) {
        var chip = document.createElement('span');
        chip.className = 'cc-form__file-chip';
        chip.innerHTML = '<span>' + file.name + '</span>';
        var removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'cc-form__file-chip-remove';
        removeBtn.textContent = '×';
        removeBtn.setAttribute('aria-label', 'Remove ' + file.name);
        removeBtn.addEventListener('click', function () {
          selectedFiles.splice(idx, 1);
          renderFilesList();
        });
        chip.appendChild(removeBtn);
        filesList.appendChild(chip);
      });

      // Hide dropzone if at 3 files
      if (selectedFiles.length >= 3) {
        multiDropzone.style.display = 'none';
      } else {
        multiDropzone.style.display = '';
      }
    }
  }

  /* ---- Restoration Form: Validation + Cart API + Checkout ---- */
  var restorationForm = document.querySelector('[data-cc-restoration-form]');
  if (restorationForm) {
    var configEl = document.querySelector('[data-cc-restoration-config]');
    var config = configEl ? JSON.parse(configEl.textContent) : { variantId: 54081485144371 };
    var UPLOAD_API = 'https://ronaldo-customer-care-production.up.railway.app';
    var formError = document.querySelector('[data-cc-form-error]');
    var submitBtn = document.querySelector('[data-cc-restoration-submit]');

    function uploadPhoto(file) {
      return fetch(UPLOAD_API + '/upload/stage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name, content_type: file.type })
      })
      .then(function (r) { return r.json(); })
      .then(function (staged) {
        var formData = new FormData();
        staged.parameters.forEach(function (p) {
          formData.append(p.name, p.value);
        });
        formData.append('file', file);
        return fetch(staged.url, { method: 'POST', body: formData })
          .then(function () { return staged.resourceUrl; });
      })
      .then(function (resourceUrl) {
        return fetch(UPLOAD_API + '/upload/finalize', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ resource_url: resourceUrl })
        })
        .then(function (r) { return r.json(); })
        .then(function (data) { return data.url; });
      });
    }

    restorationForm.addEventListener('submit', function (e) {
      e.preventDefault();

      // Clear previous errors
      restorationForm.querySelectorAll('.is-invalid').forEach(function (el) {
        el.classList.remove('is-invalid');
      });
      if (formError) formError.classList.remove('is-visible');
      if (photosError) photosError.classList.remove('is-visible');

      // Validate required fields
      var valid = true;
      var requiredInputs = restorationForm.querySelectorAll('[required]');
      requiredInputs.forEach(function (input) {
        if (!input.value.trim()) {
          input.classList.add('is-invalid');
          valid = false;
        }
      });

      // Validate photos
      if (selectedFiles.length === 0) {
        if (photosError) photosError.classList.add('is-visible');
        valid = false;
      }

      if (!valid) {
        if (formError) formError.classList.add('is-visible');
        var firstInvalid = restorationForm.querySelector('.is-invalid, .cc-form__error.is-visible');
        if (firstInvalid) {
          firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }

      // Disable submit
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Uploading photos…';
      }

      // Upload all photos, then add to cart
      var uploads = selectedFiles.map(function (file) { return uploadPhoto(file); });

      Promise.all(uploads).then(function (cdnUrls) {
        if (submitBtn) submitBtn.textContent = 'Adding to cart…';

        // Collect form data as line item properties (exclude file inputs)
        var formData = new FormData(restorationForm);
        var properties = {};
        formData.forEach(function (value, key) {
          if (value instanceof File) return;
          if (typeof value === 'string' && value.trim() !== '') {
            properties[key] = value.trim();
          }
        });

        // Store CDN URLs (clickable in Shopify admin)
        properties['_photos'] = cdnUrls.join(', ');
        properties['_photo_count'] = String(cdnUrls.length);

        // Full address as a single property for easy reading
        properties['_full_address'] = [
          properties.address1,
          properties.address2,
          properties.city + ', ' + properties.state + ' ' + properties.zip
        ].filter(Boolean).join(', ');

        // Build a native form POST to /cart/add
        var variantId = config.variantId;
        var nativeForm = document.createElement('form');
        nativeForm.method = 'POST';
        nativeForm.action = '/cart/add';
        nativeForm.style.display = 'none';

        function addHidden(name, value) {
          var input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = value;
          nativeForm.appendChild(input);
        }

        addHidden('id', variantId);
        addHidden('quantity', '1');
        addHidden('return_to', '/checkout');

        Object.keys(properties).forEach(function (key) {
          addHidden('properties[' + key + ']', properties[key]);
        });

        document.body.appendChild(nativeForm);
        nativeForm.submit();
      }).catch(function (err) {
        console.error('Photo upload failed:', err);
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = 'Submit';
        }
        if (formError) {
          formError.textContent = 'Photo upload failed. Please try again.';
          formError.classList.add('is-visible');
        }
      });
    });
  }

  /* ==================================================================
     Warranty Repair Form ($25): Source toggle, validation, cart submit
     ================================================================== */
  var warrantyForm = document.querySelector('[data-cc-warranty-form]');
  if (warrantyForm) {
    var wConfigEl = document.querySelector('[data-cc-warranty-config]');
    var wConfig = wConfigEl ? JSON.parse(wConfigEl.textContent) : { variantId: 54129680679219 };
    var W_UPLOAD_API = 'https://ronaldo-customer-care-production.up.railway.app';
    var wFormError = document.querySelector('[data-cc-warranty-form-error]');
    var wSubmitBtn = document.querySelector('[data-cc-warranty-submit]');
    var wSubmitNote = document.querySelector('[data-cc-warranty-submit-note]');
    var wSubmitTextOriginal = wSubmitBtn ? wSubmitBtn.textContent : 'Continue to checkout — $25';

    /* ---- Purchase source toggle ---- */
    var sourceCards = document.querySelectorAll('[data-cc-warranty-source]');
    var sourceHidden = document.querySelector('[data-cc-warranty-source-value]');
    var websiteFields = document.querySelector('[data-cc-warranty-website-fields]');
    var retailerFields = document.querySelector('[data-cc-warranty-retailer-fields]');
    var currentSource = 'website';

    var sidebarSteps = document.querySelector('[data-cc-warranty-sidebar-steps]');
    var websiteSidebarHTML = sidebarSteps ? sidebarSteps.innerHTML : '';
    var retailerSidebarHTML = [
      '<div class="cc-form-sidebar__step"><p class="cc-form-sidebar__step-title">Submit your request</p><p class="cc-form-sidebar__step-body">Upload your receipt and tell us about the piece.</p></div>',
      '<div class="cc-form-sidebar__step"><p class="cc-form-sidebar__step-title">We review your purchase</p><p class="cc-form-sidebar__step-body">Our team verifies your receipt within 1\u20132 business days.</p></div>',
      '<div class="cc-form-sidebar__step"><p class="cc-form-sidebar__step-title">Pay $25 when approved</p><p class="cc-form-sidebar__step-body">We\u2019ll email you a checkout link for the $25 repair fee.</p></div>',
      '<div class="cc-form-sidebar__step"><p class="cc-form-sidebar__step-title">Your label arrives with your receipt</p><p class="cc-form-sidebar__step-body">Once paid, your prepaid shipping label is attached to the confirmation email.</p></div>'
    ].join('');

    sourceCards.forEach(function (card) {
      card.addEventListener('click', function () {
        var source = this.getAttribute('data-cc-warranty-source');
        currentSource = source;
        if (sourceHidden) sourceHidden.value = source;

        sourceCards.forEach(function (c) { c.classList.remove('is-selected'); });
        this.classList.add('is-selected');

        if (source === 'website') {
          if (websiteFields) websiteFields.removeAttribute('hidden');
          if (retailerFields) retailerFields.setAttribute('hidden', '');
          if (wSubmitNote) wSubmitNote.textContent = "You'll pay $25 at checkout. Your prepaid shipping label arrives with your order confirmation.";
          if (wSubmitBtn) wSubmitBtn.textContent = wSubmitTextOriginal;
          if (sidebarSteps) sidebarSteps.innerHTML = websiteSidebarHTML;
        } else {
          if (websiteFields) websiteFields.setAttribute('hidden', '');
          if (retailerFields) retailerFields.removeAttribute('hidden');
          if (wSubmitNote) wSubmitNote.textContent = "We'll review your receipt and email you a checkout link within 1\u20132 business days.";
          if (wSubmitBtn) wSubmitBtn.textContent = 'Submit warranty request';
          if (sidebarSteps) sidebarSteps.innerHTML = retailerSidebarHTML;
        }
      });
    });

    /* ---- Issue chips (multi-select) ---- */
    var wIssueContainer = document.querySelector('[data-cc-warranty-issue-chips]');
    if (wIssueContainer) {
      var wIssueChips = wIssueContainer.querySelectorAll('[data-cc-warranty-issue-chip]');
      var wIssueHidden = document.querySelector('[data-cc-warranty-issue-value]');

      function updateWarrantyIssueValue() {
        if (!wIssueHidden) return;
        var selected = [];
        wIssueChips.forEach(function (c) {
          if (c.getAttribute('aria-pressed') === 'true') {
            selected.push(c.getAttribute('data-value'));
          }
        });
        wIssueHidden.value = selected.join(', ');
      }

      wIssueChips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          var pressed = this.getAttribute('aria-pressed') === 'true';
          this.setAttribute('aria-pressed', pressed ? 'false' : 'true');
          this.classList.toggle('is-active', !pressed);
          updateWarrantyIssueValue();
        });
      });
    }

    /* ---- Multi-file upload (piece photos) ---- */
    var wPhotoInput = document.querySelector('[data-cc-warranty-photos-input]');
    var wPhotoDropzone = document.querySelector('[data-cc-warranty-photos-dropzone]');
    var wFilesList = document.querySelector('[data-cc-warranty-files-list]');
    var wPhotosError = document.querySelector('[data-cc-warranty-photos-error]');
    var wSelectedPhotos = [];

    if (wPhotoInput && wPhotoDropzone && wFilesList) {
      ['dragenter', 'dragover'].forEach(function (evt) {
        wPhotoDropzone.addEventListener(evt, function (e) {
          e.preventDefault();
          wPhotoDropzone.classList.add('is-dragover');
        });
      });

      ['dragleave', 'drop'].forEach(function (evt) {
        wPhotoDropzone.addEventListener(evt, function (e) {
          e.preventDefault();
          wPhotoDropzone.classList.remove('is-dragover');
        });
      });

      wPhotoDropzone.addEventListener('drop', function (e) {
        wAddPhotos(e.dataTransfer.files);
      });

      wPhotoInput.addEventListener('change', function () {
        wAddPhotos(this.files);
        this.value = '';
      });

      function wAddPhotos(newFiles) {
        for (var i = 0; i < newFiles.length && wSelectedPhotos.length < 3; i++) {
          wSelectedPhotos.push(newFiles[i]);
        }
        wRenderPhotosList();
        if (wPhotosError) wPhotosError.classList.remove('is-visible');
      }

      function wRenderPhotosList() {
        wFilesList.innerHTML = '';
        wSelectedPhotos.forEach(function (file, idx) {
          var chip = document.createElement('span');
          chip.className = 'cc-form__file-chip';
          chip.innerHTML = '<span>' + file.name + '</span>';
          var removeBtn = document.createElement('button');
          removeBtn.type = 'button';
          removeBtn.className = 'cc-form__file-chip-remove';
          removeBtn.textContent = '\u00d7';
          removeBtn.setAttribute('aria-label', 'Remove ' + file.name);
          removeBtn.addEventListener('click', function () {
            wSelectedPhotos.splice(idx, 1);
            wRenderPhotosList();
          });
          chip.appendChild(removeBtn);
          wFilesList.appendChild(chip);
        });

        if (wSelectedPhotos.length >= 3) {
          wPhotoDropzone.style.display = 'none';
        } else {
          wPhotoDropzone.style.display = '';
        }
      }
    }

    /* ---- Receipt file upload (retailer only) ---- */
    var wReceiptInput = document.querySelector('[data-cc-warranty-receipt-input]');
    var wReceiptDropzone = document.querySelector('[data-cc-warranty-receipt-dropzone]');
    var wReceiptPreview = document.querySelector('[data-cc-warranty-receipt-preview]');
    var wReceiptName = document.querySelector('[data-cc-warranty-receipt-name]');
    var wReceiptRemove = document.querySelector('[data-cc-warranty-receipt-remove]');
    var wReceiptError = document.querySelector('[data-cc-warranty-receipt-error]');
    var wReceiptFile = null;

    if (wReceiptInput && wReceiptDropzone) {
      ['dragenter', 'dragover'].forEach(function (evt) {
        wReceiptDropzone.addEventListener(evt, function (e) {
          e.preventDefault();
          wReceiptDropzone.classList.add('is-dragover');
        });
      });

      ['dragleave', 'drop'].forEach(function (evt) {
        wReceiptDropzone.addEventListener(evt, function (e) {
          e.preventDefault();
          wReceiptDropzone.classList.remove('is-dragover');
        });
      });

      wReceiptDropzone.addEventListener('drop', function (e) {
        if (e.dataTransfer.files.length > 0) {
          wReceiptFile = e.dataTransfer.files[0];
          wShowReceiptPreview(wReceiptFile);
        }
      });

      wReceiptInput.addEventListener('change', function () {
        if (this.files.length > 0) {
          wReceiptFile = this.files[0];
          wShowReceiptPreview(wReceiptFile);
        }
      });

      function wShowReceiptPreview(file) {
        if (wReceiptName) wReceiptName.textContent = file.name;
        if (wReceiptPreview) wReceiptPreview.removeAttribute('hidden');
        wReceiptDropzone.style.display = 'none';
        if (wReceiptError) wReceiptError.classList.remove('is-visible');
      }

      if (wReceiptRemove) {
        wReceiptRemove.addEventListener('click', function () {
          wReceiptFile = null;
          wReceiptInput.value = '';
          if (wReceiptPreview) wReceiptPreview.setAttribute('hidden', '');
          wReceiptDropzone.style.display = '';
        });
      }
    }

    /* ---- Upload helper (same as restoration form) ---- */
    function wUploadPhoto(file) {
      return fetch(W_UPLOAD_API + '/upload/stage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name, content_type: file.type })
      })
      .then(function (r) { return r.json(); })
      .then(function (staged) {
        var formData = new FormData();
        staged.parameters.forEach(function (p) {
          formData.append(p.name, p.value);
        });
        formData.append('file', file);
        return fetch(staged.url, { method: 'POST', body: formData })
          .then(function () { return staged.resourceUrl; });
      })
      .then(function (resourceUrl) {
        return fetch(W_UPLOAD_API + '/upload/finalize', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ resource_url: resourceUrl })
        })
        .then(function (r) { return r.json(); })
        .then(function (data) { return data.url; });
      });
    }

    /* ---- Form submission ---- */
    warrantyForm.addEventListener('submit', function (e) {
      e.preventDefault();

      // Clear previous errors
      warrantyForm.querySelectorAll('.is-invalid').forEach(function (el) {
        el.classList.remove('is-invalid');
      });
      if (wFormError) wFormError.classList.remove('is-visible');
      if (wPhotosError) wPhotosError.classList.remove('is-visible');
      if (wReceiptError) wReceiptError.classList.remove('is-visible');

      // Validate required fields
      var valid = true;
      var requiredInputs = warrantyForm.querySelectorAll('[required]');
      requiredInputs.forEach(function (input) {
        if (!input.value.trim()) {
          input.classList.add('is-invalid');
          valid = false;
        }
      });

      // Validate photos
      if (wSelectedPhotos.length === 0) {
        if (wPhotosError) wPhotosError.classList.add('is-visible');
        valid = false;
      }

      // Source-specific validation
      if (currentSource === 'website') {
        var orderInput = document.querySelector('[data-cc-warranty-order-input]');
        if (orderInput && !orderInput.value.trim()) {
          orderInput.classList.add('is-invalid');
          valid = false;
        }
      } else {
        var retailerInput = document.getElementById('cc-w-retailer');
        if (retailerInput && !retailerInput.value.trim()) {
          retailerInput.classList.add('is-invalid');
          valid = false;
        }
        if (!wReceiptFile) {
          if (wReceiptError) wReceiptError.classList.add('is-visible');
          valid = false;
        }
      }

      if (!valid) {
        if (wFormError) wFormError.classList.add('is-visible');
        var firstInvalid = warrantyForm.querySelector('.is-invalid, .cc-form__error.is-visible');
        if (firstInvalid) {
          firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }

      // Disable submit
      if (wSubmitBtn) {
        wSubmitBtn.disabled = true;
        wSubmitBtn.textContent = currentSource === 'website' ? 'Verifying order\u2026' : 'Uploading photos\u2026';
      }

      // For website purchases, verify the order exists and is within 1 year
      var verifyStep;
      if (currentSource === 'website') {
        var orderNum = document.querySelector('[data-cc-warranty-order-input]').value.trim().replace(/^#/, '');
        verifyStep = fetch(W_UPLOAD_API + '/warranty/verify/' + encodeURIComponent(orderNum))
          .then(function (r) {
            if (r.status === 404) {
              throw { type: 'order_not_found', orderNum: orderNum };
            }
            if (!r.ok) throw { type: 'server_error' };
            return r.json();
          })
          .then(function (data) {
            if (!data.eligible) {
              throw { type: 'order_too_old', orderDate: data.order_date };
            }
            return data;
          });
      } else {
        verifyStep = Promise.resolve(null);
      }

      var verifiedOrder = null;
      var wSuccessEl = document.querySelector('[data-cc-warranty-success]');

      // Helper: collect form fields into a flat object
      function collectFormFields() {
        var formData = new FormData(warrantyForm);
        var fields = {};
        formData.forEach(function (value, key) {
          if (value instanceof File) return;
          if (typeof value === 'string' && value.trim() !== '') {
            fields[key] = value.trim();
          }
        });
        fields['_full_address'] = [
          fields.address1, fields.address2,
          fields.city + ', ' + fields.state + ' ' + fields.zip
        ].filter(Boolean).join(', ');
        return fields;
      }

      // Helper: submit review request to Railway API (no checkout)
      function submitForReview(cdnUrls, receiptUrl, reviewReason) {
        if (wSubmitBtn) wSubmitBtn.textContent = 'Submitting for review\u2026';
        var fields = collectFormFields();

        var payload = {
          email: fields.email,
          first_name: fields.first_name,
          last_name: fields.last_name,
          phone: fields.phone || '',
          full_address: fields['_full_address'],
          piece_name: fields.piece_name,
          issue: fields.issue,
          details: fields.details || '',
          photos: cdnUrls.join(', '),
          photo_count: String(cdnUrls.length),
          purchase_source: currentSource === 'retailer' ? 'retailer' : 'website'
        };

        if (currentSource === 'retailer') {
          payload.retailer_name = fields.retailer_name || '';
          payload.receipt_url = receiptUrl || '';
        } else {
          payload.order_number = (fields.order_number || '').replace(/^#/, '');
          payload.review_reason = reviewReason || 'Order not found in system';
        }

        return fetch(W_UPLOAD_API + '/warranty/request', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }).then(function (r) {
          if (!r.ok) throw new Error('submit failed');
          return r.json();
        });
      }

      // Helper: show success message, hide form
      function showSuccess() {
        warrantyForm.style.display = 'none';
        if (wSuccessEl) {
          wSuccessEl.removeAttribute('hidden');
          wSuccessEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }

      verifyStep.then(function (data) {
        if (data) verifiedOrder = data;
        if (wSubmitBtn) wSubmitBtn.textContent = 'Uploading photos\u2026';

        // Build upload list: photos + receipt (if retailer)
        var uploads = wSelectedPhotos.map(function (file) { return wUploadPhoto(file); });
        var receiptUpload = (currentSource === 'retailer' && wReceiptFile)
          ? wUploadPhoto(wReceiptFile)
          : Promise.resolve(null);

        return Promise.all([Promise.all(uploads), receiptUpload]);
      }).then(function (results) {
        var cdnUrls = results[0];
        var receiptUrl = results[1];

        // Retailer path: submit for review, no checkout
        if (currentSource === 'retailer') {
          return submitForReview(cdnUrls, receiptUrl).then(showSuccess);
        }

        // Website verified path: add to cart and checkout
        if (wSubmitBtn) wSubmitBtn.textContent = 'Adding to cart\u2026';

        var fields = collectFormFields();
        var properties = {};
        Object.keys(fields).forEach(function (k) { properties[k] = fields[k]; });

        properties['_photos'] = cdnUrls.join(', ');
        properties['_photo_count'] = String(cdnUrls.length);
        properties['_Warranty'] = 'Yes';
        properties['_Purchase_Source'] = 'ronaldojewelry.com';
        properties['_Order_Number'] = (fields.order_number || '').replace(/^#/, '');

        var nativeForm = document.createElement('form');
        nativeForm.method = 'POST';
        nativeForm.action = '/cart/add';
        nativeForm.style.display = 'none';

        function addHidden(name, value) {
          var input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = value;
          nativeForm.appendChild(input);
        }

        addHidden('id', wConfig.variantId);
        addHidden('quantity', '1');
        addHidden('return_to', '/checkout');

        properties['_Admin_Note'] = 'WARRANTY REPAIR ($25) | Order ' + verifiedOrder.order_name + ' placed ' + verifiedOrder.order_date + ' (verified)';

        Object.keys(properties).forEach(function (key) {
          addHidden('properties[' + key + ']', properties[key]);
        });

        document.body.appendChild(nativeForm);
        nativeForm.submit();
      }).catch(function (err) {
        console.error('Warranty form error:', err);

        // Order not found: upload photos and submit for review (no checkout)
        if (err && err.type === 'order_not_found') {
          if (wSubmitBtn) { wSubmitBtn.disabled = true; wSubmitBtn.textContent = 'Uploading photos\u2026'; }
          if (wFormError) wFormError.classList.remove('is-visible');

          var reviewUploads = wSelectedPhotos.map(function (file) { return wUploadPhoto(file); });
          Promise.all(reviewUploads).then(function (cdnUrls) {
            return submitForReview(cdnUrls, null, 'Order not found in system');
          }).then(showSuccess).catch(function () {
            if (wSubmitBtn) { wSubmitBtn.disabled = false; wSubmitBtn.textContent = wSubmitTextOriginal; }
            if (wFormError) { wFormError.textContent = 'Something went wrong. Please try again.'; wFormError.classList.add('is-visible'); }
          });
          return;
        }

        if (wSubmitBtn) {
          wSubmitBtn.disabled = false;
          wSubmitBtn.textContent = currentSource === 'website' ? wSubmitTextOriginal : 'Submit warranty request';
        }
        if (wFormError) {
          if (err && err.type === 'order_too_old') {
            var orderInput = document.querySelector('[data-cc-warranty-order-input]');
            if (orderInput) orderInput.classList.add('is-invalid');
            wFormError.textContent = 'Your order' + (err.orderDate ? ' from ' + err.orderDate : '') + ' is outside the 1-year warranty window. You can still get your piece restored for $85 \u2014 ';
            var restoreLink = document.createElement('a');
            restoreLink.href = '/pages/restoration';
            restoreLink.textContent = 'start a restoration request';
            restoreLink.style.color = 'inherit';
            restoreLink.style.textDecoration = 'underline';
            wFormError.appendChild(restoreLink);
            wFormError.appendChild(document.createTextNode('.'));
          } else {
            wFormError.textContent = 'Something went wrong. Please try again.';
          }
          wFormError.classList.add('is-visible');
          wFormError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    });
  }

})();
